import React, { ComponentPropsWithoutRef } from "react";
import styled from "styled-components";
import { Row } from "react-flexbox-grid/dist/react-flexbox-grid";

interface SpaceProps {
  children?: React.ReactNode;
  margins?: [number, number, number, number];
  paddings?: [number, number, number, number];
  maxHeight?: React.CSSProperties["maxHeight"];
  height?: React.CSSProperties["height"];
  display?: React.CSSProperties["display"];
  isInline?: boolean;
}

const MULTIPLIER = 4;

const getMargins = (margins?: [number, number, number, number]) => {
  if (!margins) {
    return {};
  }

  return {
    marginTop: `${MULTIPLIER * margins[0]}px`,
    marginRight: `${MULTIPLIER * margins[1]}px`,
    marginBottom: `${MULTIPLIER * margins[2]}px`,
    marginLeft: `${MULTIPLIER * margins[3]}px`,
  };
};

const getPaddings = (paddings?: [number, number, number, number]) => {
  if (!paddings) {
    return {};
  }

  return {
    paddingTop: `${MULTIPLIER * paddings[0]}px`,
    paddingRight: `${MULTIPLIER * paddings[1]}px`,
    paddingBottom: `${MULTIPLIER * paddings[2]}px`,
    paddingLeft: `${MULTIPLIER * paddings[3]}px`,
  };
};

export const Space: React.FunctionComponent<SpaceProps> = ({
  maxHeight,
  margins,
  paddings,
  children,
  height,
  display,
  isInline,
}) => (
  <div
    style={{
      ...getMargins(margins),
      ...getPaddings(paddings),
      maxHeight,
      height,
      display: display || (isInline ? "inline" : "block"),
    }}
  >
    {children}
  </div>
);

interface CenteredDivProps extends SpaceProps {
  width?: string;
}
export const CenteredDiv: React.FunctionComponent<CenteredDivProps> = ({
  margins,
  paddings,
  children,
  width,
  isInline,
}) => (
  <div
    style={{
      justifyContent: "center",
      width: "100%",
      ...getMargins(margins),
      ...getPaddings(paddings),
      display: isInline ? "inline" : "flex",
    }}
  >
    {width ? <div style={{ width }}>{children}</div> : children}
  </div>
);

export const CenteredRow: React.FunctionComponent<CenteredDivProps> = ({
  children,
  ...rest
}) => (
  <Row>
    <CenteredDiv {...rest}>{children}</CenteredDiv>
  </Row>
);

interface FlexProps extends ComponentPropsWithoutRef<"div"> {
  children?: React.ReactNode;
  alignItems?: React.CSSProperties["alignItems"];
  alignSelf?: React.CSSProperties["alignSelf"];
  justifyContent?: React.CSSProperties["justifyContent"];
  direction?: React.CSSProperties["flexDirection"];
  width?: React.CSSProperties["width"];
  height?: React.CSSProperties["height"];
  grow?: React.CSSProperties["flexGrow"];
  basis?: React.CSSProperties["flexBasis"];
  wrap?: React.CSSProperties["flexWrap"];
  shrink?: React.CSSProperties["flexShrink"];
  margins?: [number, number, number, number];
  paddings?: [number, number, number, number];
  gap?: React.CSSProperties["gap"];
  minWidth?: React.CSSProperties["minWidth"];
  minHeight?: React.CSSProperties["minHeight"];
  backgroundColor?: React.CSSProperties["backgroundColor"];
}

export const Flex: React.FunctionComponent<FlexProps> = ({
  alignItems = "unset",
  justifyContent = "unset",
  direction = "unset",
  gap = "unset",
  width = "auto",
  height = "auto",
  margins,
  paddings,
  grow,
  shrink,
  basis,
  alignSelf,
  wrap = "nowrap",
  children,
  minWidth,
  minHeight,
  style,
  backgroundColor,
  ...rest
}) => (
  <div
    style={{
      display: "flex",
      alignItems,
      justifyContent,
      flexDirection: direction,
      width,
      height,
      gap,
      minWidth,
      minHeight,
      flexShrink: shrink,
      flexGrow: grow,
      ...(alignSelf && { alignSelf }),
      ...(basis !== undefined && { flexBasis: basis }),
      flexWrap: wrap,
      ...getMargins(margins),
      ...getPaddings(paddings),
      position: "relative",
      backgroundColor,
      ...style,
    }}
    {...rest}
  >
    {children}
  </div>
);

interface IGridProps {
  flex?: boolean;
}

export const Grid = styled.div<IGridProps>`
  display: ${({ flex }) => (flex ? "flex" : "inherit")};
  height: 100%;
  min-height: calc(100vh - ${({ theme }) => theme.layout.navbarHeight}px);
  position: relative;
`;
